import { computed, signal, Signal } from '@preact/signals-react';
import { injectable } from 'inversify';

import { Success } from '@vp/common/core/OperationResult';
import { ViewModel, ViewModelDispose } from '@vp/common/ui/ViewModel';
import { sleep } from '@vp/common/util';
import { RemoveMediaDto } from '@vp/manager/gallery/core/dto/RemoveMediaDto';
import { UploadMediaType } from '@vp/manager/gallery/core/dto/UploadMediaType';
import { GalleryManagerPort } from '@vp/manager/gallery/core/interface/GalleryManagerPort';
import { GalleryManagerSection } from '@vp/manager/gallery/ui/GalleryManagerSection';
import { GalleryManagerPreviewProps, PhotoPreview, VideoPreview } from '@vp/manager/gallery/ui/preview/GalleryManagerPreview';
import { ProfileManagerPort } from '@vp/manager/profile/core/interface/ProfileManagerPort';
import { AppNotificationService } from '@vp/notification/AppNotificationService';
import { ProfilePhotoLiveStatus, ProfilePhotoModel, ProfilePhotoType } from '@vp/profile/core/model/ProfilePhotoModel';

@injectable()
export class GalleryManagerPreviewViewModel extends ViewModel<GalleryManagerPreviewProps> implements ViewModelDispose {
  readonly deleteDialogShown: Signal<boolean> = signal(false);
  readonly animateDialogShown: Signal<boolean> = signal(false);
  readonly makeStaticDialogShown: Signal<boolean> = signal(false);

  readonly removing: Signal<boolean> = signal(false);
  readonly animating: Signal<boolean> = signal(false);
  readonly makingStatic: Signal<boolean> = signal(false);

  readonly sectionName: Signal<string> = computed(() => (this.props.value?.section === GalleryManagerSection.Photos ? 'фото' : 'видео'));

  readonly currentPhoto: Signal<ProfilePhotoModel> = computed(() => this.extractCurrentPhoto());

  readonly animateButtonLoading: Signal<boolean> = computed(() => {
    const photo = this.currentPhoto.value!;
    return this.animating.value || this.makingStatic.value || photo.liveStatus === ProfilePhotoLiveStatus.Pending;
  });

  readonly showAnimatePhotoButton: Signal<boolean> = computed(() => this.props.value?.section === GalleryManagerSection.Photos);

  /**
   * Controls whether the "Animate" button should be disabled.
   *
   * Rules:
   * - Disabled if the photo is in Pending state (animation in progress).
   * - Disabled if there are no animation attempts left AND:
   *    - the photo is not Live, AND
   *    - there is no previously saved animation (liveStatus !== Ready).
   * - Enabled if the photo has a saved animation (Ready), even if its type is Static,
   *   since re-animating does not consume additional attempts.
   */
  readonly animateButtonDisabled: Signal<boolean> = computed(() => {
    const photo = this.currentPhoto.value!;
    const isPending = photo.liveStatus === ProfilePhotoLiveStatus.Pending;
    const isLive = photo.liveStatus === ProfilePhotoLiveStatus.Ready && photo.type === ProfilePhotoType.Live;

    const hasSavedAnimation = photo.liveStatus === ProfilePhotoLiveStatus.Ready;
    const noAttemptsLeft = !this.galleryManagerPort.hasPhotoAnimationAttempts();

    return isPending || (noAttemptsLeft && !isLive && !hasSavedAnimation);
  });

  readonly animateButtonText: Signal<string> = computed(() => {
    return this.isActiveLivePhoto() ? 'Вернуть' : `Анимировать`;
  });

  readonly animateDialogText: Signal<string> = computed(() => {
    const [attemptsLeft, total] = this.getAnimationQuota();
    return `У вас есть ограниченное количество анимаций для фотографий (${attemptsLeft} из ${total}). После запуска процесс займёт немного времени, поэтому результат появится не сразу — обновите страницу позже, и фото станет анимированным. В любой момент вы можете отменить анимацию, и изображение вернётся к исходному виду.`;
  });

  private controller?: AbortController;

  constructor(
    private readonly galleryManagerPort: GalleryManagerPort,
    private readonly profileManagerPort: ProfileManagerPort,
    private readonly notificationService: AppNotificationService,
  ) {
    super();
  }

  dispose(): void {
    this.controller?.abort();
  }

  onAnimateButtonClick = (): void => {
    if (this.isActiveLivePhoto()) {
      this.showMakeStaticDialog();
    } else {
      this.showAnimateDialog();
    }
  };

  showDeleteDialog = (): void => {
    this.deleteDialogShown.value = true;
  };

  closeDeleteDialog = (): void => {
    this.deleteDialogShown.value = false;
  };

  showAnimateDialog = (): void => {
    this.animateDialogShown.value = true;
  };

  closeAnimateDialog = (): void => {
    this.animateDialogShown.value = false;
  };

  showMakeStaticDialog = (): void => {
    this.makeStaticDialogShown.value = true;
  };

  closeMakeStaticDialog = (): void => {
    this.makeStaticDialogShown.value = false;
  };

  animate = async (): Promise<void> => {
    this.animating.value = true;
    await this.handleAnimate();
    this.animating.value = false;
  };

  makeStatic = async (): Promise<void> => {
    this.makingStatic.value = true;
    await this.handleMakeStatic();
    this.makingStatic.value = false;
  };

  remove = async (): Promise<void> => {
    this.removing.value = true;
    await this.handleRemove();
    this.removing.value = false;
  };

  isPhotoPreview = (item: PhotoPreview | VideoPreview): item is PhotoPreview => {
    return !Reflect.has(item, 'thumbnail');
  };

  private async handleMakeStatic(): Promise<void> {
    this.controller = new AbortController();
    const result = await this.galleryManagerPort.makeStatic(this.props.value!.item.id, this.controller);
    await sleep(250);

    if (result instanceof Success) {
      this.showMakeStaticSuccessMessage();
      this.closeMakeStaticDialog();
    } else {
      this.showMakeStaticErrorMessage(result.message);
    }
  }

  private async handleAnimate(): Promise<void> {
    this.controller = new AbortController();
    const result = await this.galleryManagerPort.makeLive(this.props.value!.item.id, this.controller);
    await sleep(250);

    if (result instanceof Success) {
      this.showAnimateSuccessMessage();
      this.closeAnimateDialog();
    } else {
      this.showAnimateErrorMessage(result.message);
    }
  }

  private async handleRemove(): Promise<void> {
    this.controller = new AbortController();
    const result = await this.galleryManagerPort.remove(this.toRemoveDto(), this.controller);
    await sleep(250);

    if (result instanceof Success) {
      this.closeDeleteDialog();
      this.props.value?.close();
      this.showDeleteSuccessMessage();
    } else {
      this.showDeleteErrorMessage(result.message);
    }
  }

  private showDeleteSuccessMessage(): void {
    const name = this.isPhotoPreview(this.props.value!.item) ? 'Фото' : 'Видео';
    this.notificationService.enqueue({ variant: 'success', message: `${name} было удалено` });
  }

  private showAnimateSuccessMessage(): void {
    const message = 'Анимирование фотографии было успешно запланировано';
    this.notificationService.enqueue({ variant: 'success', message });
  }

  private showMakeStaticSuccessMessage(): void {
    const message = 'Анимация отменена. Фото стало оригинальным';
    this.notificationService.enqueue({ variant: 'success', message });
  }

  private showDeleteErrorMessage(message: string): void {
    const name = this.isPhotoPreview(this.props.value!.item) ? 'фото' : 'видео';
    const secondaryMessage = message || `Не удалось удалить ${name}`;
    this.notificationService.enqueue({ variant: 'error', message: 'Что-то пошло не так', secondaryMessage });
  }

  private showAnimateErrorMessage(message: string): void {
    const secondaryMessage = message || 'Не удалось анимировать изображение';
    this.notificationService.enqueue({ variant: 'error', message: 'Что-то пошло не так', secondaryMessage });
  }

  private showMakeStaticErrorMessage(message: string): void {
    const secondaryMessage = message || 'Не удалось отменить анимацию изображения';
    this.notificationService.enqueue({ variant: 'error', message: 'Что-то пошло не так', secondaryMessage });
  }

  private getAnimationQuota(): [number, number] {
    const active = this.profileManagerPort.active.value!;
    return active.liveMedia.getPhotosQuota();
  }

  private extractCurrentPhoto(): ProfilePhotoModel {
    const photoId = this.props.value!.item.id;
    return this.galleryManagerPort.getPhotoOfActiveProfileBy(photoId);
  }

  private isActiveLivePhoto(): boolean {
    const photo = this.currentPhoto.value!;
    return photo.liveStatus === ProfilePhotoLiveStatus.Ready && photo.type === ProfilePhotoType.Live;
  }

  private toRemoveDto(): RemoveMediaDto {
    const id = this.props.value!.item.id;
    const profileId = this.profileManagerPort.active.value!.id;
    const type = this.props.value!.section === GalleryManagerSection.Photos ? UploadMediaType.Photos : UploadMediaType.Videos;
    return new RemoveMediaDto({ id, type, profileId });
  }
}
